import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { NotificationBell } from '../ui/NotificationBell';
import MaterialIcon from '../ui/MaterialIcon';
import { LegalFooter } from '../ui/LegalFooter';
import { logEvent } from '../../lib/activityLogger';
import { AssociationSwitcher } from '../shared/AssociationSwitcher';

const ROLE_CONFIG = {
  admin: {
    label: 'Administrator',
    badge: 'bg-secondary/10 text-forest-900 border border-forest-300',
    sidebar: 'bg-sidebar-bg',
    accent: 'bg-amber-gold',
  },
  management: {
    label: 'Management',
    badge: 'bg-amber-50 text-amber-800 border border-amber-200',
    sidebar: 'bg-sidebar-bg',
    accent: 'bg-secondary',
  },
  board: {
    label: 'Board Member',
    badge: 'bg-blue-50 text-blue-800 border border-blue-200',
    sidebar: 'bg-sidebar-bg',
    accent: 'bg-forest-400',
  },
  member: {
    label: 'Homeowner',
    badge: 'bg-stone-100 text-stone-700 border border-stone-300',
    sidebar: 'bg-sidebar-bg',
    accent: 'bg-secondary',
  },
};

const SWITCH_TIERS = [
  {
    role: 'admin', label: 'Administrator',
    color: 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100'
  },
  {
    role: 'management', label: 'Management',
    color: 'text-secondary bg-secondary/10 border-secondary/30 hover:bg-forest-100'
  },
  {
    role: 'board', label: 'Board Member',
    color: 'text-secondary bg-secondary/10 border-secondary/30 hover:bg-forest-100'
  },
  {
    role: 'member', label: 'Homeowner',
    color: 'text-slate-700 bg-slate-50 border-slate-200 hover:bg-slate-100'
  },
];

const PortalLayout = ({ title, tabs, activeTab, onTabChange, children, userRole, notifications = {}, colorScheme = 'member' }) => {
  const { user, logout, switchTier, resetTier, currentTier,
    isAdminViewingOtherTier, isPlatformOwner,
    impersonating, stopImpersonating } = useAuth();
  const { hoa } = useHOA();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTierSwitch, setShowTierSwitch] = useState(false);
  const menuButtonRef = useRef(null);
  const previousSidebarOpenRef = useRef(false);
  const lastLoggedSectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncBodyScrollLock = () => {
      const isMobileNav = window.matchMedia('(max-width: 1023px)').matches;
      document.body.style.overflow = sidebarOpen && isMobileNav ? 'hidden' : '';
    };

    syncBodyScrollLock();
    window.addEventListener('resize', syncBodyScrollLock);
    return () => {
      window.removeEventListener('resize', syncBodyScrollLock);
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  useEffect(() => {
    if (previousSidebarOpenRef.current && !sidebarOpen) {
      menuButtonRef.current?.focus();
    }
    previousSidebarOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  useEffect(() => {
    const actorId = impersonating?.id || user?.id;
    if (!actorId || !activeTab) return;

    const sectionKey = `${actorId}:${userRole}:${activeTab}`;
    if (lastLoggedSectionRef.current === sectionKey) return;

    lastLoggedSectionRef.current = sectionKey;
    logEvent('section_view', {
      section: activeTab,
      role: userRole,
      actor_roles: [userRole].filter(Boolean),
    });
  }, [activeTab, impersonating?.id, user?.id, userRole]);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setShowTierSwitch(false);
  }, []);

  const config = ROLE_CONFIG[userRole] || ROLE_CONFIG.member;

  const isAdmin = user?.portalRole === 'admin';
  const showTierSwitcherUI = isAdmin || isPlatformOwner;
  const activeTier = currentTier || user?.portalRole;

  const handleTierSwitch = (tier) => {
    if (tier.role === user?.portalRole) {
      resetTier();
    } else {
      switchTier(tier.role);
    }
    setShowTierSwitch(false);
    navigate('/portal');
  };

  const handleSignOut = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    try {
      stopImpersonating?.();
      resetTier?.();
      await logout();
    } finally {
      closeSidebar();
      navigate('/', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex bg-main-bg font-sans overflow-x-clip">
      {impersonating && (
        <div className="fixed top-0 right-0 z-[200] bg-amber-500 text-white shadow-warm sm:left-0 sm:w-full sm:flex sm:items-center sm:justify-between sm:px-4 sm:py-2 sm:text-sm sm:font-medium rounded-bl-xl px-3 py-1.5 text-xs font-semibold">
          <div className="flex items-center gap-1.5">
            <span className="hidden sm:inline">👁</span>
            <span>
              <span className="hidden sm:inline">Viewing as </span>
              <strong>{impersonating.full_name}</strong>
              <span className="hidden sm:inline">&nbsp;({impersonating.role})</span>
            </span>
          </div>
          <button
            onClick={stopImpersonating}
            className="px-3 py-1 bg-white text-amber-600 rounded-full font-semibold text-xs hover:bg-amber-50 transition-colors min-h-[36px]"
          >
            ← Return as Admin
          </button>
        </div>
      )}

      {/* Sidebar */}
      <>
        {/* Mobile overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeSidebar}
              className="fixed inset-0 bg-primary-dark/70 backdrop-blur-sm z-40 lg:hidden"
            />
          )}
        </AnimatePresence>

        <aside className={`
          fixed top-0 left-0 h-[100dvh] max-h-[100dvh] safe-dvh w-[min(88vw,320px)] lg:w-[260px] z-50 flex min-h-0 flex-col overflow-hidden bg-sidebar-bg
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
          aria-label="Portal navigation"
        >

          {/* Brand area */}
          <div className="shrink-0 px-6 lg:px-8 pb-4 pt-[max(1.5rem,env(safe-area-inset-top))]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 lg:w-10 lg:h-10 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
                  <MaterialIcon name="landscape" size={24} className="text-white" />
                </div>
                <div>
                  <div className="text-white text-lg font-bold leading-tight">
                    {hoa?.name || 'HOA Portal'}
                  </div>
                  {hoa?.settings?.location && (
                    <div className="text-white/50 text-xs">{hoa.settings.location}</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={closeSidebar}
                className="lg:hidden touch-target rounded-full text-white/60 hover:text-white hover:bg-white/10 flex items-center justify-center"
                aria-label="Close navigation"
              >
                <MaterialIcon name="close" size={24} />
              </button>
            </div>

            {/* Role / tier pill */}
            <div className="mt-4 relative z-40">
              {showTierSwitcherUI ? (
                <button
                  onClick={() => setShowTierSwitch(!showTierSwitch)}
                  className="w-full flex items-center justify-between px-4 py-2 border border-white/20 rounded-full text-white text-sm font-medium hover:bg-white/5 transition-colors"
                >
                  <span>{config.label}</span>
                  {isAdminViewingOtherTier && (
                    <span className="text-[10px] uppercase opacity-70 mr-1">Preview</span>
                  )}
                  <MaterialIcon name="expand_more" size={18} className={`opacity-70 transition-transform ${showTierSwitch ? 'rotate-180' : ''}`} />
                </button>
              ) : (
                <div className="w-full flex items-center justify-between px-4 py-2 border border-white/20 rounded-full text-white text-sm font-medium">
                  <span>{config.label}</span>
                </div>
              )}

              {/* Tier switch dropdown */}
              <AnimatePresence>
                {showTierSwitch && showTierSwitcherUI && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 right-0 mt-2 bg-white rounded-2xl warm-tactile-shadow border border-amber-100/40 overflow-hidden z-50"
                  >
                    <div className="p-2 border-b border-slate-100">
                      <p className="text-xs text-slate-400 uppercase tracking-wide font-semibold px-2 py-1">Switch Demo Tier</p>
                    </div>
                    <div className="p-1.5 space-y-1">
                      {SWITCH_TIERS.map((tier) => (
                        <button
                          key={tier.role}
                          onClick={() => handleTierSwitch(tier)}
                          className={`w-full min-h-[48px] flex items-center justify-between px-3 py-2 rounded-2xl text-sm font-medium border transition-all ${tier.color} ${tier.role === activeTier ? 'ring-2 ring-forest-400' : ''}`}
                        >
                          <span>{tier.label}</span>
                          {tier.role === activeTier && <span className="text-xs opacity-60">Current</span>}
                        </button>
                      ))}
                      {isAdminViewingOtherTier && (
                        <button
                          onClick={() => { resetTier(); setShowTierSwitch(false); navigate('/portal'); }}
                          className="w-full mt-1 min-h-[48px] px-3 py-2 rounded-2xl text-xs text-slate-500 hover:bg-slate-50 border border-slate-200 transition-all"
                        >
                          ↩ Return to Admin View
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Super Admin: jump between associations */}
            <AssociationSwitcher />
          </div>

          {/* Nav */}
          <nav
            data-testid="portal-sidebar-nav"
            className="portal-sidebar-scroll min-h-0 flex-1 py-2 overflow-y-auto overflow-x-clip overscroll-contain"
          >
            {tabs.map((tab, i) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => { onTabChange(tab.id); closeSidebar(); }}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className={`
                    w-full text-left transition-all
                    ${isActive
                      ? 'relative flex items-center gap-3 px-6 lg:px-8 py-4 min-h-[56px] bg-sidebar-active text-sidebar-accent font-semibold text-sm'
                      : 'flex items-center gap-3 px-6 lg:px-8 py-4 min-h-[56px] text-white/50 hover:text-white hover:bg-white/5 font-medium text-sm'
                    }
                  `}
                >
                  {isActive && <div className="sidebar-active-indicator" />}

                  {/* Icon: prefer materialIcon prop, fall back to Lucide */}
                  {tab.materialIcon
                    ? <MaterialIcon name={tab.materialIcon} size={22} className="flex-shrink-0" />
                    : Icon && <Icon className="w-4 h-4 flex-shrink-0" />
                  }

                  <span className="flex-1">{tab.label}</span>

                  {notifications[tab.id] > 0 && (
                    <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {notifications[tab.id] > 99 ? '99+' : notifications[tab.id]}
                    </span>
                  )}

                  {isActive && (
                    <MaterialIcon name="chevron_right" size={16} className="ml-auto opacity-40" />
                  )}
                </motion.button>
              );
            })}
          </nav>

          {/* Sidebar footer — user info + sign out */}
          <div
            data-testid="portal-sidebar-footer"
            className="shrink-0 px-6 py-4 border-t border-white/5 pb-[max(1rem,env(safe-area-inset-bottom))]"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center flex-shrink-0">
                <MaterialIcon name="person" size={22} className="text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-sm truncate">{user?.name || 'User'}</div>
                <div className="text-white/40 text-xs truncate">{user?.email || ''}</div>
              </div>
            </div>
            <button
              type="button"
              onClick={handleSignOut}
              data-testid="sign-out-btn"
              className="w-full min-h-[44px] flex items-center gap-2 text-white/60 hover:text-white text-sm font-medium transition-colors mt-3 rounded-full py-2"
            >
              <MaterialIcon name="logout" size={19} />
              Sign Out
            </button>
          </div>
        </aside>
      </>

      {/* Main content */}
      <div className={`ml-0 lg:ml-[260px] flex-1 bg-main-bg min-h-screen flex flex-col w-full max-w-full overflow-x-clip${impersonating ? ' pt-10' : ''}`}>
        {/* Top bar */}
        <header className="sticky lg:static top-0 z-30 w-full flex-shrink-0 flex justify-between items-center gap-3 bg-main-bg/95 backdrop-blur px-4 sm:px-6 md:px-12 pt-[max(1rem,env(safe-area-inset-top))] pb-4 md:py-10 border-b border-amber-100/40 lg:border-b-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              ref={menuButtonRef}
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden touch-target rounded-full text-primary-dark hover:bg-section-bg flex items-center justify-center"
              aria-label="Open navigation"
              aria-expanded={sidebarOpen}
            >
              <MaterialIcon name="menu" size={24} />
            </button>
            <h1 className="font-bold text-primary-dark text-xl sm:text-2xl truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
            <NotificationBell />
            <div className="hidden sm:flex w-11 h-11 rounded-full bg-white items-center justify-center warm-tactile-shadow">
              <MaterialIcon name="person" size={20} className="text-primary-dark" />
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 w-full max-w-full overflow-x-clip px-4 md:px-12 pb-[max(4rem,env(safe-area-inset-bottom))]">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>

        {/* Permanent Terms / Privacy links; inside the content column so the sidebar is never overlaid */}
        <LegalFooter
          tone="light"
          linkState={{ fromPortal: true }}
          className="flex-shrink-0 px-4 md:px-12 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]"
        />
      </div>
    </div>
  );
};

export default PortalLayout;
